import React, { useEffect, useState } from "react";
import Loader from "../../components/Loader";
import Calling from "../../api/Calling";
import { toast } from "react-toastify";
import generateCaptcha from "../../components/captcha";
import formatDateTime from "../../components/formatDateTime";
import FileUpload from "../../components/ul/FileUpload";
import {
  imageSizeExceededMessage,
  imageType,
  invalidImageMessage,
  MAX_FILE_SIZE_IMAGE,
} from "../../components/imageType";
import img1 from "../../Image/notificationson.svg";
import NotFound from "../../components/ul/NotFound";
import UploadButton from "../../components/ul/UploadButton";
import DeleteButton from "../../components/ul/DeleteButton";
import { LuBell, LuHistory, LuTrash2, LuFlame, LuCircleCheck, LuTriangleAlert, LuUpload } from "react-icons/lu";

const Notification = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [fb, setFb] = useState({ configured: false, project_id: "" });
  const [fbUploading, setFbUploading] = useState(false);
  const [fileProcessing, setFileProcessing] = useState({
    image: false,
  });
  const [fileStatus, setFileStatus] = useState({
    image: null, // 'success', 'error', or null
  });
  const [file, setFile] = useState(null);
  const [formInfo, setFormInfo] = useState({
    title: "",
    message: "",
    url: "",
  });
  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.GetNotificationHistory();
      if (resp?.status) {
        setData(resp.data);
      } else {
        toast.error(resp?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  const fetchFbStatus = async () => {
    try {
      const resp = await Calling.GetFirebaseStatus();
      if (resp?.status) {
        setFb({ configured: !!resp.configured, project_id: resp.project_id || "" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchFbStatus();
  }, []);

  const handleServiceAccountUpload = async (e) => {
    const selected = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!selected) return;
    if (!selected.name.endsWith(".json")) {
      return toast.error("Please select the Firebase serviceAccount .json file");
    }
    setFbUploading(true);
    try {
      const content = await selected.text();
      const resp = await Calling.UploadServiceAccount(content);
      if (resp?.status) {
        toast.success(resp?.message || "Firebase configured");
        setFb({ configured: !!resp.configured, project_id: resp.project_id || "" });
      } else {
        toast.error(resp?.message || "Failed to configure Firebase");
      }
    } catch (error) {
      console.error(error);
      toast.error("Could not read the file");
    } finally {
      setFbUploading(false);
    }
  };

  const handleDeleteSelected = async (id) => {
    const content = `You are about to delete a notification . To confirm, please solve this math problem.`;
    const check = generateCaptcha(content);
    if (check) {
      try {
        setLoader(true);
        let body = {};
        if (id) {
          body.id = [id];
        } else {
          body.id = selectedUsers;
        }

        const response = await Calling.DeleteNotification(body);
        if (response?.status) {
          toast.success(
            response?.message || "notification deleted successfully !"
          );
          setSelectedUsers([]);
          setSelectAll(false); // Reset "Select All"
          fetchData();
        } else {
          toast.error(response?.message);
        }
      } catch (error) {
        console.error("Error deleting users:", error);
      } finally {
        setLoader(false);
      }
    } else {
      toast.error("Captcha answer is incorrect", { autoClose: 2000 });
    }
  };

  const handleSelectAll = () => {
    if (!selectAll) {
      const allUserIds = data.map((user) => user._id);
      setSelectedUsers(allUserIds);
    } else {
      setSelectedUsers([]);
    }
    setSelectAll(!selectAll);
  };
  const handleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter((userId) => userId !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("title", formInfo?.title || "");
      formData.append("message", formInfo?.message || "");
      formData.append("url", formInfo?.url || "");
      if (file) {
        formData.append("image", file);
      }
      const response = await Calling.SendNotification(formData);
      if (response?.status) {
        toast.success(response?.message || "Notification sent successfully !");
        setFormInfo({ title: "", message: "", url: "" });
        setFile(null);
        setFileProcessing({
          image: false,
        });
        setFileStatus({
          image: null, // 'success', 'error', or null
        });
      } else {
        toast.error(response?.message);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      fetchData();
      setLoader(false);
    }
  };

  const handleChangefile = (e) => {
    const selectedFile = e.target.files[0];

    setFileProcessing({ image: true }); // Start processing

    // Validate the MIME type
    if (selectedFile && !imageType.includes(selectedFile.type)) {
      toast.error(invalidImageMessage);
      setFile(null); // Reset file if invalid
      setFileProcessing({ image: false }); // Stop processing
      setFileStatus({ image: "error" }); // Set error status
      e.target.value = "";
      return;
    } else {
      if (selectedFile?.size > MAX_FILE_SIZE_IMAGE) {
        setFile(null);
        setFileProcessing({ image: false }); // Stop processing
        setFileStatus({ image: "error" });
        toast.error(imageSizeExceededMessage);
        e.target.value = "";
        return;
      }
      setFile(selectedFile); // Set the file if valid
      setFileStatus({ image: "success" }); // Set success status
      setFileProcessing({ image: false }); // Stop processing
    }
  };
  const handleFormChange = (e) => {
    setFormInfo({ ...formInfo, [e.target.name]: e.target.value });
  };

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="ml-0 md:ml-56 text-main-font mt-16 px-3">
          <div className="ui-card-solid w-full mt-4">
            <div className="p-4 md:p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-11 h-11 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 shrink-0">
                  <LuBell size={22} />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-extrabold text-main-font">
                    Notification Send
                  </h1>
                  <p className="text-xs sm:text-sm text-main-subfont">
                    Compose and push a notification to all users
                  </p>
                </div>
              </div>
              <hr className="border-main-border mb-4" />

              {/* Firebase (FCM) setup — upload serviceAccount.json without touching the server */}
              <div
                className={`rounded-2xl border p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                  fb.configured
                    ? "border-brand-teal/30 bg-teal-500/10"
                    : "border-amber-400/40 bg-amber-400/10"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white ${
                      fb.configured ? "bg-brand-teal" : "bg-amber-500"
                    }`}
                  >
                    <LuFlame size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-main-font flex items-center gap-1.5">
                      Push (Firebase)
                      {fb.configured ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-teal">
                          <LuCircleCheck size={13} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600">
                          <LuTriangleAlert size={13} /> Not configured
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-main-subfont truncate">
                      {fb.configured
                        ? `Connected to project: ${fb.project_id || "—"}. Notifications are delivered to phones.`
                        : "Upload your Firebase serviceAccount.json to enable real push delivery."}
                    </p>
                  </div>
                </div>
                <label className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-teal-500 to-cyan-500 shadow-md shadow-teal-500/25 hover:brightness-110 cursor-pointer transition disabled:opacity-60">
                  <LuUpload size={16} />
                  {fbUploading
                    ? "Uploading..."
                    : fb.configured
                    ? "Replace Key"
                    : "Upload serviceAccount.json"}
                  <input
                    type="file"
                    accept=".json,application/json"
                    className="hidden"
                    disabled={fbUploading}
                    onChange={handleServiceAccountUpload}
                  />
                </label>
              </div>

              <form
                onSubmit={handleSubmit}
                encType="multipart/form-data"
                className="bg-main-bg border border-main-border rounded-2xl p-4 sm:p-5"
              >
                {/* Title Field */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center mb-4">
                  <label
                    className="md:col-span-1 text-main-font font-medium mb-2 md:mb-0"
                    htmlFor="title"
                  >
                    Title:
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formInfo?.title}
                    onChange={(e) => handleFormChange(e)}
                    id="title"
                    className="ui-input w-full md:col-span-11 p-2 rounded-xl mt-1"
                    placeholder="Enter Title"
                    required
                  />
                </div>

                {/* Message Field */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center mb-4">
                  <label
                    className="md:col-span-1 text-main-font font-medium mb-2 md:mb-0"
                    htmlFor="message"
                  >
                    Message:
                  </label>
                  <input
                    type="text"
                    name="message"
                    value={formInfo?.message}
                    onChange={(e) => handleFormChange(e)}
                    id="message"
                    className="ui-input w-full md:col-span-11 p-2 rounded-xl mt-1"
                    placeholder="Enter Message"
                    required
                  />
                </div>

                {/* URL Field */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center mb-4">
                  <label
                    className="md:col-span-1 text-main-font font-medium mb-2 md:mb-0"
                    htmlFor="url"
                  >
                    URL:
                  </label>
                  <input
                    type="text"
                    name="url"
                    value={formInfo?.url}
                    onChange={(e) => handleFormChange(e)}
                    id="url"
                    className="ui-input w-full md:col-span-11 p-2 rounded-xl mt-1"
                    placeholder="Enter URL"
                  />
                </div>

                {/* Icon Upload */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center mb-4">
                  <label
                    className="md:col-span-1 text-main-font font-medium mb-2 md:mb-0"
                    htmlFor="icon"
                  >
                    Icon:
                  </label>
                  <div className="relative w-full md:col-span-11">
                    {fileProcessing["image"] && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="loader"></div>
                      </div>
                    )}
                    {fileStatus["image"] === "error" && (
                      <div className="absolute inset-0 flex mt-3 ml-[-20px] text-xl text-red-500">
                        ✘
                      </div>
                    )}
                    {fileStatus["image"] === "success" && (
                      <div className="absolute inset-0 flex mt-3 ml-[-20px] text-xl text-green-500">
                        ✓
                      </div>
                    )}
                    <FileUpload
                      id="image"
                      name="image"
                      accept="image/png, image/gif"
                      onChange={handleChangefile}
                      required={false}
                      multiple={false}
                    />
                    <p
                      className={`text-[12px] mt-1 ${
                        fileStatus["image"] === "error"
                          ? "text-main-delete"
                          : "text-main-subfont"
                      }`}
                    >
                      Only PNG, JPG, JPEG files are allowed.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex">
                  <UploadButton className="w-full sm:w-auto" />
                </div>
              </form>

              {/* Data */}
              <div className="overflow-x mt-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30 shrink-0">
                      <LuHistory size={18} />
                    </div>
                    <h2 className="text-base sm:text-lg font-bold text-main-font">
                      Notification History
                    </h2>
                  </div>
                  {selectedUsers.length > 0 && (
                    <button
                      onClick={() => handleDeleteSelected(null)}
                      className="bg-red-500 text-white px-4 py-2 rounded-xl shadow-lg shadow-red-500/20 hover:brightness-110 flex items-center gap-2 font-semibold"
                    >
                      <LuTrash2 className="text-base" />
                      Delete Selected
                    </button>
                  )}
                </div>
              </div>
            </div>

            <hr className="border-main-border border-[2px]" />

            {/* Responsive Table */}
            <div className="overflow-x-auto">
              <table className="w-full mt-4 rounded-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">
                  <tr>
                    <th className="ps-6 py-3 text-left text-sm font-medium uppercase">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">
                      #
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">
                      Icon
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase">
                      Title
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">
                      Message
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">
                      Url
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase hidden md:table-cell">
                      Date
                    </th>
                    <th className="px-3 py-3 text-left text-sm font-medium uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.length > 0 ? (
                    data.map((record, index) => (
                      <tr
                        key={record._id}
                        className="border-b border-main-border text-main-font hover:bg-main-bg"
                      >
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(record._id)}
                            onChange={() => handleSelectUser(record._id)}
                          />
                        </td>
                        <td className="p-2 hidden md:table-cell">
                          {index + 1}
                        </td>
                        <td className="p-2 hidden md:table-cell">
                          <img
                            src={record.n_icon || img1}
                            alt="icon"
                            className="w-16 h-16 rounded-full"
                          />
                        </td>
                        <td className="p-2">{record.n_title}</td>
                        <td className="p-2 hidden md:table-cell">
                          {record.n_message}
                        </td>
                        <td className="p-2 hidden md:table-cell">
                          <a
                            href={record.n_url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-3 py-2 rounded-xl shadow-lg shadow-teal-500/30 hover:brightness-110"
                          >
                            Url
                          </a>
                        </td>
                        <td className="p-2 hidden md:table-cell">
                          {formatDateTime(record.createdAt)}
                        </td>
                        <td className="p-2">
                          <DeleteButton
                            onClick={() => handleDeleteSelected(record._id)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="text-center border border-main-border px-4 py-2"
                      >
                        <NotFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notification;
